import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UserRole } from '@/state/store/auth/index.type';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { UserPlus } from 'lucide-react';
import { AdminSidebar } from './admin-sidebar';
import { AdminTopbar } from './admin-topbar';
import { AdminDashboardQuickActionsMenu } from './admin-dashboard-quick-actions-menu';
import { AdminTemplatesTopbarActions } from '../templates/components/admin-templates-topbar-actions';
import { useAdminUsersUiStore } from '../users/store/use-admin-users-ui-store';

interface AdminShellProps {
  userRole: UserRole;
}

export function AdminShell({ userRole }: AdminShellProps) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  // Exact matches — AdminTopbar is itself md:hidden, so any topbar action
  // rendered here only appears on mobile.
  const isDashboardRoute = useMatch('/admin');
  const isTemplatesListRoute = useMatch('/admin/templates');
  const isUsersListRoute = useMatch('/admin/users');
  const openInviteDialog = useAdminUsersUiStore((s) => s.openInviteDialog);

  // Full-screen for builder pages
  const isBuilderPage = location.pathname.includes('builder');
  if (isBuilderPage) {
    return <Outlet />;
  }

  let topbarAction: React.ReactNode | undefined;
  if (isDashboardRoute) {
    topbarAction = <AdminDashboardQuickActionsMenu userRole={userRole} />;
  } else if (isTemplatesListRoute) {
    topbarAction = <AdminTemplatesTopbarActions />;
  } else if (isUsersListRoute) {
    // Just the trigger button — the actual dialog stays mounted in
    // UsersAdminClient. Zustand bridges the two: tap here, page opens.
    topbarAction = (
      <Button
        variant="primary"
        size="sm"
        onClick={openInviteDialog}
        aria-label={t('ADMIN_USERS_ACTIONS_INVITE')}
      >
        <UserPlus className="size-4" />
        {t('ADMIN_USERS_ACTIONS_INVITE')}
      </Button>
    );
  }

  return (
    <div className="h-full">
      <div className="flex h-full overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block">
          <AdminSidebar userRole={userRole} />
        </aside>

        {/* Mobile Sidebar (Radix Dialog drawer) — always mounted so
            open/close animations and focus management work properly. */}
        <AdminSidebar
          userRole={userRole}
          isMobile
          open={mobileSidebarOpen}
          onOpenChange={setMobileSidebarOpen}
        />

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <AdminTopbar onMenuClick={() => setMobileSidebarOpen(true)} action={topbarAction} />
          <main className="flex-1 overflow-y-auto">
            <div className="py-4 md:py-6 px-2 md:px-4 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
